import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { LayoutContext } from '../context';
import * as api from './api';
import './moonmeet.css';
import MeetingTimeNotice from './MeetingTime';

const Context = createContext(null);
export const useMoonMeet = () => useContext(Context);
export const isMeetingPath = path => path === '/moonmeet' || path.startsWith('/moonmeet/join/');

function RemoteAudio({ track }) {
  const ref = useRef(null);
  useEffect(() => { const el = ref.current; track.attach(el); return () => track.detach(el); }, [track]);
  return <audio ref={ref} autoPlay/>;
}

export default function MoonMeetProvider({ children }) {
  const { profile } = useContext(LayoutContext), location = useLocation(), navigate = useNavigate();
  const [session, setSession] = useState(null), [snapshot, setSnapshot] = useState(null), [room, setRoom] = useState(null);
  const [phase, setPhase] = useState('idle'), [error, setError] = useState(''), [revision, setRevision] = useState(0), [expanded, setExpanded] = useState(true);
  const [board, setBoard] = useState(null), [panel, setPanel] = useState(() => window.innerWidth >= 900 ? 'chat' : '');
  const active = useRef(null), rtc = useRef(null), busy = useRef(false), generation = useRef(0), options = useRef({ mic: true, camera: false });
  const refreshRef = useRef(null), profileId = useRef(profile?.uuid || profile?.id);
  const bump = useCallback(() => setRevision(x => x + 1), []);
  const disconnect = useCallback(() => { const current = rtc.current; rtc.current = null; current?.disconnect(); setRoom(null); }, []);
  const leave = useCallback(async () => {
    const current = active.current; generation.current++; active.current = null; disconnect(); busy.current = false;
    setSession(null); setSnapshot(null); setBoard(null); setPhase('idle'); setError('');
    if (current) { try { await api.leave(current); } catch { /* Local media always stops even if the request fails. */ } }
  }, [disconnect]);
  useEffect(() => () => { rtc.current?.disconnect(); }, []);
  useEffect(() => {
    const id = profile?.uuid || profile?.id;
    if (profileId.current && !id && active.current?.member?.guest === false) void leave();
    profileId.current = id;
  }, [profile, leave]);
  useEffect(() => {
    const unload = e => { if (rtc.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', unload); return () => window.removeEventListener('beforeunload', unload);
  }, []);

  async function join(invite, name, media = {}) {
    if (active.current && active.current.invite !== invite) throw Error('Сначала завершите текущий звонок.');
    if (active.current) return;
    const result = await api.join(invite, name), value = { ...result, invite };
    options.current = { mic: media.mic !== false, camera: media.camera ?? result.meeting.mode === 'video' };
    active.current = value; setSession(value); setSnapshot(result); setPhase(result.member.approved ? 'lobby' : 'waiting'); setError(''); setExpanded(true);
  }
  const refresh = useCallback(async () => {
    const current = active.current; if (!current) return;
    try {
      const value = await api.state(current);
      if (active.current !== current) return;
      setSnapshot(value);
      if (['ended', 'cancelled'].includes(value.meeting.status)) { generation.current++; disconnect(); setPhase('ended'); }
      else if (!value.member.approved) setPhase(value.member.banned ? 'blocked' : 'waiting');
      else if (!rtc.current && !busy.current) setPhase(previous => ['waiting', 'lobby'].includes(previous) ? 'lobby' : previous);
    } catch (e) {
      if (active.current !== current) return;
      if ([401, 403].includes(e.response?.status)) { generation.current++; disconnect(); setPhase('blocked'); }
      setError(api.error(e));
    }
  }, [disconnect]);
  refreshRef.current = refresh;
  useEffect(() => {
    if (!session) return;
    void refresh();
    const interval = setInterval(() => void refresh(), 2500);
    let socket;
    try {
      socket = new WebSocket(api.socket(session.meeting.id));
      socket.onopen = () => socket.send(JSON.stringify({ kind: 'join', authorization: api.auth(session.invite) }));
      socket.onmessage = () => void refreshRef.current();
      socket.onerror = () => {}; // REST polling remains authoritative.
    } catch {}
    const ping = setInterval(() => { if (socket?.readyState === 1) socket.send(JSON.stringify({ kind: 'ping' })); }, 25000);
    return () => { clearInterval(interval); clearInterval(ping); socket?.close(); };
  }, [session, refresh]);

  const connect = useCallback(async () => {
    const current = active.current;
    if (!current || busy.current || rtc.current) return;
    busy.current = true; const attempt = ++generation.current; setPhase('connecting'); setError('');
    let next;
    try {
      const { Room, RoomEvent, VideoPresets } = await import('livekit-client');
      const grant = await api.connect(current);
      if (active.current !== current || attempt !== generation.current) return;
      next = new Room({ adaptiveStream: true, dynacast: true,
        videoCaptureDefaults: { resolution: VideoPresets.h360.resolution },
        audioCaptureDefaults: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        publishDefaults: { videoCodec: 'vp8', videoEncoding: { maxBitrate: 450000, maxFramerate: 20 }, screenShareEncoding: { maxBitrate: 1200000, maxFramerate: 10 }, audioPreset: { maxBitrate: 32000 }, dtx: true },
      });
      rtc.current = next;
      const events = ['ParticipantConnected','ParticipantDisconnected','TrackSubscribed','TrackUnsubscribed','LocalTrackPublished','LocalTrackUnpublished','TrackMuted','TrackUnmuted','ActiveSpeakersChanged','ParticipantMetadataChanged','ParticipantPermissionsChanged','ConnectionQualityChanged','AudioPlaybackStatusChanged'];
      events.forEach(key => { if (RoomEvent[key]) next.on(RoomEvent[key], bump); });
      next.on(RoomEvent.Reconnecting, () => { if (rtc.current === next) setPhase('reconnecting'); });
      next.on(RoomEvent.Reconnected, () => { if (rtc.current === next) { setPhase('connected'); void refreshRef.current(); } });
      next.on(RoomEvent.Disconnected, reason => {
        if (rtc.current !== next) return;
        rtc.current = null; setRoom(null); setPhase('disconnected');
        setError('Соединение завершено. Можно подключиться повторно, если встреча ещё открыта.');
        void refreshRef.current();
      });
      await next.connect(grant.url, grant.token);
      if (active.current !== current || attempt !== generation.current) { next.disconnect(); return; }
      setRoom(next); setPhase('connected'); bump();
      try { await next.startAudio(); } catch {}
      try { if (options.current.mic) await next.localParticipant.setMicrophoneEnabled(true); } catch { setError('Микрофон не включён. Разрешите доступ или выберите другое устройство в настройках.'); }
      try { if (options.current.camera) await next.localParticipant.setCameraEnabled(true); } catch { setError('Камера не включена. Можно продолжить со звуком и включить её позже.'); }
      bump(); void refreshRef.current();
    } catch (e) {
      if (rtc.current === next) { rtc.current = null; next?.disconnect(); }
      if (active.current === current && attempt === generation.current) { setPhase('error'); setError(api.error(e)); }
    } finally { if (attempt === generation.current) busy.current = false; }
  }, [bump]);
  useEffect(() => {
    if (phase === 'lobby' && snapshot?.member?.approved && (snapshot.member.role === 'owner' || snapshot.meeting.opened_at)) void connect();
  }, [phase, snapshot, connect]);

  async function action(fn) {
    try { setError(''); const result = await fn(); if (result?.media_pending) setError('Ограничение сохранено. Отключение медиа ожидает восстановления связи с LiveKit.'); await refresh(); return result; }
    catch (e) { setError(api.error(e)); throw e; }
  }
  async function media(kind) {
    const value = rtc.current; if (!value) return;
    try {
      setError(''); const p = value.localParticipant;
      if (kind === 'mic') await p.setMicrophoneEnabled(!p.isMicrophoneEnabled);
      if (kind === 'camera') await p.setCameraEnabled(!p.isCameraEnabled);
      if (kind === 'screen') {
        if (!navigator.mediaDevices?.getDisplayMedia) throw Error('Этот браузер не поддерживает отправку экрана. Можно смотреть чужой экран и работать на общей доске.');
        await p.setScreenShareEnabled(!p.isScreenShareEnabled, { audio: true, resolution: { width: 1280, height: 720, frameRate: 10 } });
      }
      bump();
    } catch (e) { setError(e.name === 'NotAllowedError' ? 'Доступ не разрешён или выбор экрана отменён.' : api.error(e)); }
  }
  async function openBoard(scope) {
    if (!active.current) return;
    const value = await action(() => api.board(active.current, scope)); setBoard(value); setPanel('board'); return value;
  }
  const visible = isMeetingPath(location.pathname) && new URLSearchParams(location.search).get('view') !== 'meetings', participants = room ? [room.localParticipant, ...room.remoteParticipants.values()] : [];
  const audioTracks = participants.filter(p => !p.isLocal).flatMap(p => [...p.audioTrackPublications.values()].filter(pub => pub.track && !pub.isMuted).map(pub => pub.track));
  const returnToCall = () => navigate(session?.member.role === 'owner' ? '/moonmeet?meeting=' + session.meeting.id : '/moonmeet/join/' + session.invite);
  const value = { session, snapshot, room, phase, error, setError, revision, participants, expanded, setExpanded, board, panel, setPanel,
    join, leave, refresh, connect, media, openBoard, action, returnToCall, bump };
  return <Context.Provider value={value}>{children}
    <MeetingTimeNotice snapshot={snapshot} refresh={refresh}/>
    {audioTracks.map(track => <RemoteAudio key={track.sid} track={track}/>)}
    {session && !visible && <div className="mm-mini" role="region" aria-label="Текущий звонок MoonMeet"><span className="mm-live-dot"/><button onClick={returnToCall}><strong>MoonMeet</strong><span>{snapshot?.meeting.title} · {phase === 'connected' ? 'На связи' : phase === 'waiting' ? 'Ожидаем входа' : 'Проверить соединение'}</span></button><button aria-label="Микрофон звонка" onClick={() => media('mic')}>{room?.localParticipant.isMicrophoneEnabled ? 'Микр. вкл.' : 'Микр. выкл.'}</button><button className="mm-mini-leave" onClick={() => void leave()}>Выйти</button></div>}
  </Context.Provider>;
}
